import type { LfgLftApplicationResponse } from "@/shared/api/lfg-lft-types";

/**
 * Заявки на доске (KAN-1355). Бэкенд отдаёт входящие одним списком: приглашение от команды
 * игроку (InviteLfgPlayer) и заявка игрока в команду (ApplyToLft) — в диалоге это две вкладки.
 */

/** Как у подписи в объявлении и на бэкенде (LfgLftApplicationService.MessageMaxLength). */
export const RESPONSE_MESSAGE_MAX = 240;

export const APPLICATION_TABS = ["teams", "players", "mine"] as const;
export type ApplicationTab = (typeof APPLICATION_TABS)[number];

export const APPLICATION_STATUSES = ["Pending", "Accepted", "Declined", "Withdrawn"] as const;
export type ApplicationStatus = (typeof APPLICATION_STATUSES)[number];

export function parseApplicationStatus(raw: string): ApplicationStatus {
  const hit = APPLICATION_STATUSES.find((s) => s === raw);
  if (!hit) throw new Error(`Unknown application status: ${raw}`);
  return hit;
}

export function splitIncoming(items: readonly LfgLftApplicationResponse[]): {
  teams: LfgLftApplicationResponse[];
  players: LfgLftApplicationResponse[];
} {
  return {
    teams: items.filter((a) => a.kind === "InviteLfgPlayer"),
    players: items.filter((a) => a.kind === "ApplyToLft"),
  };
}

/** Счётчик кнопки «Заявки»: только ждущие решения — мэтч и отказ на него не влияют. */
export function countNewApplications(items: readonly LfgLftApplicationResponse[] | undefined): number {
  return (items ?? []).filter((a) => a.status === "Pending").length;
}

/** Действия над заявкой по её состоянию (макет): кто что может сделать. */
export type ApplicationAction = "accept" | "decline" | "message" | "cancel" | "restore" | "withdraw";

/** `postActive: false` — объявление закрыто (например, место занято): решение уже не вернуть. */
export function actionsFor(status: ApplicationStatus, side: "incoming" | "outgoing", postActive = true): ApplicationAction[] {
  if (side === "outgoing") {
    if (status === "Pending") return ["withdraw"];
    if (status === "Accepted") return ["message"];
    return [];
  }
  if (status === "Pending") return ["accept", "decline"];
  if (status === "Accepted") return postActive ? ["message", "cancel"] : ["message"];
  if (status === "Declined") return postActive ? ["restore"] : [];
  return [];
}

/** Новые сверху, внутри статуса — по времени: ждущие решения не тонут среди решённых. */
export function sortForInbox(items: readonly LfgLftApplicationResponse[]): LfgLftApplicationResponse[] {
  const weight = (s: string) => (s === "Pending" ? 0 : s === "Accepted" ? 1 : 2);
  return [...items].sort((a, b) => weight(a.status) - weight(b.status) || b.createdAt.localeCompare(a.createdAt));
}
